import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Monitor, 
  Cog, 
  Pill, 
  Heart,
  ChevronRight,
  Award,
  Briefcase,
  Check,
  Loader2
} from 'lucide-react';

const VERTICAL_ICONS = {
  'it-vertical': Monitor,
  'engineering-vertical': Cog,
  'pharma-vertical': Pill,
  'healthcare-vertical': Heart
};

const VERTICAL_COLORS = {
  'it-vertical': 'from-blue-500 to-cyan-500',
  'engineering-vertical': 'from-orange-500 to-amber-500',
  'pharma-vertical': 'from-emerald-500 to-green-500',
  'healthcare-vertical': 'from-rose-500 to-pink-500'
};

const JobCategorySelectionModal = ({ isOpen, onClose, onCategorySelected }) => {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certification/job-categories');
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      toast.error('Failed to load job categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = async () => {
    if (!selectedCategory) return;

    try {
      setConfirming(true);
      const res = await api.post('/rpat/certification/select-category', {
        category_id: selectedCategory.id
      });
      
      toast.success(`You've selected ${selectedCategory.name} as your specialization!`);
      onCategorySelected?.(res.data);
      onClose();
    } catch (err) {
      console.error('Failed to select category:', err);
      toast.error(err.response?.data?.detail || 'Failed to select category');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600">
              <Award className="h-6 w-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl">Choose Your Specialization</DialogTitle>
              <DialogDescription className="text-base mt-1">
                Congratulations on completing Phase 4! Select a job category to continue your certification journey.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-4">
          {/* Info Banner */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-cyan-800 mb-2">What happens next?</h4>
            <ul className="text-sm text-cyan-700 space-y-1">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                <span><strong>Phase 5:</strong> Master 5 jobs from your chosen category</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                <span><strong>Phase 6:</strong> Complete 5 sourcing lab challenges</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                <span><strong>Phase 7:</strong> Complete 5 role play scenarios</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                <span><strong>Phase 8:</strong> Final assessment & TekWissen Certified Recruiter certificate</span>
              </li>
            </ul>
          </div>

          {/* Category Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category) => {
                const Icon = VERTICAL_ICONS[category.id] || Briefcase;
                const gradientColor = VERTICAL_COLORS[category.id] || 'from-gray-500 to-slate-500';
                const isSelected = selectedCategory?.id === category.id;

                return (
                  <Card
                    key={category.id}
                    className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                      isSelected 
                        ? 'ring-2 ring-cyan-500 shadow-lg' 
                        : 'hover:border-cyan-300'
                    }`}
                    onClick={() => setSelectedCategory(category)}
                    data-testid={`category-${category.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-r ${gradientColor} shrink-0`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg">{category.name}</h3>
                            {isSelected && (
                              <div className="p-1 bg-cyan-500 rounded-full">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {category.description}
                          </p>
                          <div className="flex items-center gap-3 mt-3">
                            <Badge variant="secondary" className="text-xs">
                              {category.category_count || 0} categories
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {category.skill_count || 0} skills
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={confirming}>
              Decide Later
            </Button>
            <Button
              onClick={handleSelectCategory}
              disabled={!selectedCategory || confirming}
              className="bg-gradient-to-r from-cyan-600 to-blue-600"
              data-testid="confirm-category-btn"
            >
              {confirming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  Continue with {selectedCategory?.name || 'Selection'}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            You can change your selection later, but progress will be reset.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobCategorySelectionModal;
